import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';

import { IonicModule } from '@ionic/angular/lazy';
import { Subject, takeUntil } from 'rxjs';
import { addIcons } from 'ionicons';
import { micOutline, closeOutline, checkmarkOutline } from 'ionicons/icons';
import { LoggerService } from '../../services/logger.service';
import { TranscriptionService } from '../../services/transcription.service';
import { VoiceCaptureService } from '../../services/voice-capture.service';

type VoiceInputState = 'idle' | 'recording' | 'transcribing' | 'error';

/**
 * Mic button + recording pill for voice-to-text chat input.
 *
 * Lives in Solo chrome adjacent to the ChatKit surface (the ChatKit composer
 * is shadow-DOM encapsulated, so the control cannot render inside it). On a
 * successful transcription the text is emitted for the host surface to inject
 * via chatkit.setComposerValue() so the user reviews before sending.
 */
@Component({
    selector: 'app-voice-input',
    imports: [IonicModule],
    template: `
    @if (supported) {
      <div class="voice-input">
        @if (state === 'idle') {
          <button
            class="mic-button"
            type="button"
            title="Voice input"
            aria-label="Start voice input"
            [disabled]="disabled"
            (click)="startRecording()"
            >
            <ion-icon name="mic-outline"></ion-icon>
          </button>
        }
        @if (state === 'recording') {
          <div class="voice-pill recording">
            <span class="rec-dot" aria-hidden="true"></span>
            <span class="elapsed">{{ formattedElapsed }}</span>
            @if (secondsRemaining !== null && secondsRemaining <= 30) {
              <span class="limit-warning">
                {{ secondsRemaining }}s left
              </span>
            }
            <button
              class="pill-button cancel"
              type="button"
              title="Cancel recording"
              aria-label="Cancel recording"
              (click)="cancelRecording()"
              >
              <ion-icon name="close-outline"></ion-icon>
            </button>
            <button
              class="pill-button confirm"
              type="button"
              title="Stop and transcribe"
              aria-label="Stop and transcribe"
              (click)="stopAndTranscribe()"
              >
              <ion-icon name="checkmark-outline"></ion-icon>
            </button>
          </div>
        }
        @if (state === 'transcribing') {
          <div class="voice-pill transcribing">
            <ion-spinner name="crescent"></ion-spinner>
            <span>Transcribing…</span>
          </div>
        }
        @if (state === 'error') {
          <div class="voice-pill error" (click)="dismissError()">
            <span>{{ errorMessage }}</span>
            <ion-icon name="close-outline"></ion-icon>
          </div>
        }
      </div>
    }
    `,
    styles: [`
    .voice-input {
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }

    .mic-button {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      border: 1px solid var(--glass-border, rgba(16, 26, 20, 0.12));
      background: var(--bg-primary, #FCFBF7);
      color: var(--accent, var(--ion-color-primary));
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 2px 10px rgba(16, 26, 20, 0.14);
      transition: transform 0.12s ease, box-shadow 0.12s ease;
    }

    .mic-button:hover:not(:disabled) {
      transform: scale(1.06);
      box-shadow: 0 4px 14px rgba(16, 26, 20, 0.2);
    }

    .mic-button:disabled {
      opacity: 0.5;
      cursor: default;
    }

    .mic-button ion-icon {
      font-size: 22px;
    }

    .voice-pill {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 12px;
      border-radius: 22px;
      background: var(--bg-primary, #FCFBF7);
      border: 1px solid var(--glass-border, rgba(16, 26, 20, 0.12));
      box-shadow: 0 2px 10px rgba(16, 26, 20, 0.14);
      color: var(--text-primary, var(--ion-text-color));
      font-size: 0.9rem;
      font-variant-numeric: tabular-nums;
      animation: voice-pill-in 0.16s ease-out;
    }

    @keyframes voice-pill-in {
      from { opacity: 0; transform: scale(0.92); }
      to { opacity: 1; transform: scale(1); }
    }

    .voice-pill.recording {
      border-color: rgba(var(--ion-color-danger-rgb, 235, 68, 90), 0.4);
    }

    .rec-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: var(--ion-color-danger, #eb445a);
      animation: voice-pulse 1.2s ease-in-out infinite;
    }

    @keyframes voice-pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.45; transform: scale(0.8); }
    }

    .limit-warning {
      color: var(--ion-color-danger, #eb445a);
      font-size: 0.8rem;
      font-weight: 600;
    }

    .pill-button {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      background: var(--glass, rgba(16, 26, 20, 0.06));
      color: var(--text-primary, var(--ion-text-color));
    }

    .pill-button.confirm {
      background: var(--ion-color-primary, #0E5B3A);
      color: #ffffff;
    }

    .pill-button ion-icon {
      font-size: 18px;
    }

    .voice-pill.transcribing ion-spinner {
      width: 18px;
      height: 18px;
      --color: var(--ion-color-primary, #0E5B3A);
    }

    .voice-pill.error {
      cursor: pointer;
      border-color: rgba(var(--ion-color-danger-rgb, 235, 68, 90), 0.4);
      color: var(--ion-color-danger, #eb445a);
      max-width: 280px;
    }
  `]
})
export class VoiceInputComponent implements OnInit, OnDestroy {
  /** Hard stop; mirrors the chat service's Transcription:MaxDurationSeconds. */
  @Input() maxDurationSeconds = 300;
  @Input() disabled = false;

  @Output() transcript = new EventEmitter<string>();

  state: VoiceInputState = 'idle';
  elapsedSeconds = 0;
  errorMessage = '';
  supported = false;

  private destroy$ = new Subject<void>();

  constructor(
    private voiceCapture: VoiceCaptureService,
    private transcription: TranscriptionService,
    private logger: LoggerService
  ) {}

  ngOnInit(): void {
    addIcons({ micOutline, closeOutline, checkmarkOutline });
    this.supported = this.voiceCapture.isSupported();

    this.voiceCapture.elapsed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(seconds => {
        this.elapsedSeconds = seconds;
        if (this.state === 'recording' && seconds >= this.maxDurationSeconds) {
          this.logger.info('VoiceInputComponent', 'elapsed', 'Max duration reached - auto stopping');
          this.stopAndTranscribe();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.state === 'recording') {
      this.voiceCapture.cancel();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  get formattedElapsed(): string {
    const minutes = Math.floor(this.elapsedSeconds / 60);
    const seconds = this.elapsedSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  get secondsRemaining(): number | null {
    if (this.state !== 'recording') {
      return null;
    }
    return Math.max(0, this.maxDurationSeconds - this.elapsedSeconds);
  }

  async startRecording(): Promise<void> {
    if (this.state !== 'idle' || this.disabled) {
      return;
    }

    try {
      this.elapsedSeconds = 0;
      await this.voiceCapture.start();
      this.state = 'recording';
    } catch (error) {
      this.logger.error('VoiceInputComponent', 'startRecording', 'Microphone access failed', { error });
      this.showError('Microphone unavailable. Check browser permissions.');
    }
  }

  async stopAndTranscribe(): Promise<void> {
    if (this.state !== 'recording') {
      return;
    }

    this.state = 'transcribing';

    try {
      const recording = await this.voiceCapture.stop();
      const result = await this.transcription.transcribe(recording);
      this.state = 'idle';
      this.transcript.emit(result.text);
    } catch (error) {
      this.logger.error('VoiceInputComponent', 'stopAndTranscribe', 'Transcription failed', { error });
      this.showError('Could not transcribe. Please try again.');
    }
  }

  cancelRecording(): void {
    if (this.state !== 'recording') {
      return;
    }
    this.voiceCapture.cancel();
    this.state = 'idle';
    this.elapsedSeconds = 0;
  }

  dismissError(): void {
    if (this.state === 'error') {
      this.state = 'idle';
    }
  }

  private showError(message: string): void {
    this.errorMessage = message;
    this.state = 'error';
    window.setTimeout(() => {
      if (this.state === 'error') {
        this.state = 'idle';
      }
    }, 5000);
  }
}
